import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const LanguageContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
];

export const LanguageProvider = ({ children, userType = 'admin', userId = null, token = null }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en');
  const [loading, setLoading] = useState(false);

  // Load user's preferred language from backend on mount
  useEffect(() => {
    const loadUserLanguage = async () => {
      if (!userId || !token) {
        // If no user, use localStorage or browser language
        const savedLang = localStorage.getItem('preferred_language');
        if (savedLang && SUPPORTED_LANGUAGES.some(l => l.code === savedLang)) {
          i18n.changeLanguage(savedLang);
          setCurrentLanguage(savedLang);
        }
        return;
      }

      try {
        const endpoint = userType === 'operator' 
          ? `${API_URL}/mobile/operators/${userId}/language`
          : `${API_URL}/users/${userId}/language`;
        
        const response = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const lang = response.data.preferred_language;
        if (lang && SUPPORTED_LANGUAGES.some(l => l.code === lang)) {
          i18n.changeLanguage(lang);
          setCurrentLanguage(lang);
          localStorage.setItem('preferred_language', lang);
        }
      } catch (error) {
        // If API fails, use localStorage fallback
        const savedLang = localStorage.getItem('preferred_language');
        if (savedLang) {
          i18n.changeLanguage(savedLang);
          setCurrentLanguage(savedLang);
        }
      }
    };

    loadUserLanguage();
  }, [userId, token, userType, i18n]);

  const changeLanguage = useCallback(async (langCode) => {
    if (!SUPPORTED_LANGUAGES.some(l => l.code === langCode)) return;

    setLoading(true);
    try {
      // Update i18n immediately for responsive UI
      await i18n.changeLanguage(langCode);
      setCurrentLanguage(langCode);
      localStorage.setItem('preferred_language', langCode);

      // Save to backend if user is logged in
      if (userId && token) {
        const endpoint = userType === 'operator'
          ? `${API_URL}/mobile/operators/${userId}/language`
          : `${API_URL}/users/${userId}/language`;
        
        await axios.put(
          endpoint,
          { preferred_language: langCode },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    } catch (error) {
      console.error('Failed to save language preference:', error);
      // UI still updated, just log the error
    } finally {
      setLoading(false);
    }
  }, [i18n, userId, token, userType]);

  return (
    <LanguageContext.Provider value={{ 
      currentLanguage, 
      changeLanguage, 
      loading,
      supportedLanguages: SUPPORTED_LANGUAGES 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
